import { Dispatch, MouseEvent, SetStateAction, useEffect, useState } from "react";
import { DateRangeType } from "../utils/filter-types";

type UseDateFilterOptions = {
  queryParams: {
    start_date: string;
    end_date: string;
  };
  setQueryParams: Dispatch<SetStateAction<any>>;
};

export const useDateFilter = ({ queryParams, setQueryParams }: UseDateFilterOptions) => {
  const [dateOpen, setDateOpen] = useState(false);

  const dateValue: DateRangeType = {
    from: queryParams.start_date ? new Date(queryParams.start_date) : undefined,
    to: queryParams.end_date ? new Date(queryParams.end_date) : undefined,
  };

  const [localDate, setLocalDate] = useState<DateRangeType>(dateValue);
  const hasDateSelected = !!queryParams.start_date || !!queryParams.end_date;

  useEffect(() => {
    if (!dateOpen) {
      setLocalDate(dateValue);
    }
  }, [dateOpen, queryParams.start_date, queryParams.end_date]);

  const handleDateSelect = (selectedDateRange: DateRangeType | undefined) => {
    if (!selectedDateRange) return setLocalDate(null);
    setLocalDate(selectedDateRange);
  };

  const handleDateReset = (event: MouseEvent) => {
    event.stopPropagation();
    setLocalDate(null);
    setQueryParams((prev: any) => ({
      ...prev,
      start_date: "",
      end_date: "",
      page: 1,
    }));
    setDateOpen(false);
  };

  const toStartOfDayISO = (date: Date | undefined): string => {
    if (!date) return "";
    const start = new Date(date.getFullYear(), date.getMonth(), date.getDate(), 0, 0, 0, 0);
    return start.toISOString();
  };

  const toEndOfDayISO = (date: Date | undefined): string => {
    if (!date) return "";
    const end = new Date(date.getFullYear(), date.getMonth(), date.getDate(), 23, 59, 59, 999);
    return end.toISOString();
  };

  const handleDateApply = (event: MouseEvent) => {
    event.stopPropagation();
    setQueryParams((prev: any) => ({
      ...prev,
      start_date: toStartOfDayISO(localDate?.from),
      end_date: toEndOfDayISO(localDate?.to || localDate?.from),
      page: 1,
    }));
    setDateOpen(false);
  };

  return {
    dateOpen,
    setDateOpen,
    dateValue,
    localDate,
    setLocalDate,
    hasDateSelected,
    handleDateSelect,
    handleDateReset,
    handleDateApply,
  };
};
